// ===================================
// TRENCH QUEST - Share Card Preview
// Shows 3 share card options to choose from
// ===================================

import { FC, useState, useEffect, useCallback } from 'react'
import { RetroWindow, RetroButton, PixelText } from '@/components/retro'
import {
  generateShareCard,
  downloadShareCard,
  shareToTwitter,
  type ShareCardData,
} from '@/services/shareCard'

// Different card styles/themes
const CARD_STYLES = [
  {
    id: 'hype',
    name: 'HYPE MODE',
    getMessage: (data: ShareCardData) => {
      if (data.result === 'win') {
        return getRandomFrom([
          'Built different. No cap.',
          'Trenches conquered. Few understand.',
          'From the gutter to glory.',
          'Diamond hands paid off.',
        ])
      } else {
        return getRandomFrom([
          'Got rekt but still here.',
          'The trenches took everything.',
          'NGMI... until next time.',
          'Lesson learned. Tuition paid.',
        ])
      }
    },
    getTweetText: (data: ShareCardData) => {
      if (data.result === 'win') {
        return `LFG I just hit ${data.multiplier?.toFixed(1)}x in @TrenchQuest

${formatCurrency(data.startingAmount || 0)} → ${formatCurrency(data.endingAmount || 0)}

Built different.`
      } else {
        return `Got rekt in @TrenchQuest

${formatCurrency(data.startingAmount || 0)} → ${formatCurrency(data.endingAmount || 0)}

Running it back.`
      }
    },
  },
  {
    id: 'stats',
    name: 'STATS',
    getMessage: (data: ShareCardData) => {
      const multiplier = data.multiplier || 1
      if (multiplier >= 10) return 'Generational wealth unlocked.'
      if (multiplier >= 5) return 'Five bagger secured.'
      if (multiplier >= 2) return 'Doubled up and out.'
      if (multiplier >= 1) return 'Preserved capital. Smart play.'
      return 'Markets are brutal.'
    },
    getTweetText: (data: ShareCardData) => {
      return `@TrenchQuest Session Complete

Start: ${formatCurrency(data.startingAmount || 0)}
End: ${formatCurrency(data.endingAmount || 0)}
Multiplier: ${(data.multiplier || 1).toFixed(2)}x
Decisions: ${data.daysPlayed || 0}

${data.result === 'win' ? 'WAGMI' : 'NGMI'}`
    },
  },
  {
    id: 'meme',
    name: 'DEGEN',
    getMessage: (data: ShareCardData) => {
      if (data.result === 'win') {
        return getRandomFrom([
          'ser this is wendys and im rich',
          'mom im a crypto trader now',
          'who needs a degree when you have vibes',
          'financial advisor? never heard of her',
        ])
      } else {
        return getRandomFrom([
          'at least i tried ser',
          'call that tuition',
          'it was never about the money (cope)',
          'room temperature IQ plays only',
        ])
      }
    },
    getTweetText: (data: ShareCardData) => {
      if (data.result === 'win') {
        return `imagine not playing @TrenchQuest

${(data.multiplier || 1).toFixed(1)}x gains secured

few`
      } else {
        return `@TrenchQuest: ${formatCurrency(data.startingAmount || 0)} → ${formatCurrency(data.endingAmount || 0)}

ngmi but at least it was fun`
      }
    },
  },
]

interface Props {
  data: ShareCardData
  onClose: () => void
}

interface CardOption {
  id: string
  name: string
  imageUrl: string | null
  message: string
  tweetText: string
  isLoading: boolean
  error: string | null
}

export const ShareCardPreview: FC<Props> = ({ data, onClose }) => {
  const [cardOptions, setCardOptions] = useState<CardOption[]>([])
  const [selectedCardId, setSelectedCardId] = useState<string>('hype')
  const [isGenerating, setIsGenerating] = useState(true)

  // Generate all card options on mount
  useEffect(() => {
    async function generateCards() {
      setIsGenerating(true)

      const options: CardOption[] = CARD_STYLES.map(style => ({
        id: style.id,
        name: style.name,
        imageUrl: null,
        message: style.getMessage(data),
        tweetText: style.getTweetText(data),
        isLoading: true,
        error: null,
      }))

      setCardOptions(options)

      // Generate each card in parallel
      const promises = CARD_STYLES.map(async (style, index) => {
        try {
          const cardData: ShareCardData = {
            ...data,
            customMessage: style.getMessage(data),
          }
          const imageUrl = await generateShareCard(cardData)
          return { index, imageUrl, error: null }
        } catch (err) {
          console.error(`Failed to generate ${style.id} card:`, err)
          return { index, imageUrl: null, error: 'Failed to generate' }
        }
      })

      const results = await Promise.all(promises)

      setCardOptions(prev => {
        const updated = [...prev]
        for (const result of results) {
          const card = updated[result.index]
          if (card) {
            card.imageUrl = result.imageUrl
            card.isLoading = false
            card.error = result.error
          }
        }
        return updated
      })

      setIsGenerating(false)
    }

    generateCards()
  }, [data])

  const selectedCard = cardOptions.find(c => c.id === selectedCardId)

  const handleDownload = useCallback(() => {
    if (selectedCard?.imageUrl) {
      const filename = `trench-quest-${selectedCard.id}-${Date.now()}.png`
      downloadShareCard(selectedCard.imageUrl, filename)
    }
  }, [selectedCard])

  const handleShareTwitter = useCallback(() => {
    if (selectedCard) {
      shareToTwitter(selectedCard.tweetText, 'https://trench-quest.web.app')
    }
  }, [selectedCard])

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText('https://trench-quest.web.app')
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
      <RetroWindow
        title="SHARE YOUR JOURNEY"
        className="max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClose={onClose}
      >
        <div className="space-y-4">
          {/* Card Style Selector */}
          <div className="flex flex-wrap gap-2 justify-center">
            {cardOptions.map((card) => (
              <button
                key={card.id}
                onClick={() => setSelectedCardId(card.id)}
                className={`px-4 py-2 font-pixel text-sm border-2 transition-all ${
                  selectedCardId === card.id
                    ? 'bg-game-accent text-game-bg border-game-accent'
                    : 'bg-game-panel text-game-text border-game-border hover:border-game-accent'
                }`}
              >
                {card.name}
                {card.isLoading && ' ...'}
              </button>
            ))}
          </div>

          {/* Preview Area */}
          <div className="relative bg-game-bg border-2 border-game-border p-2">
            {selectedCard?.isLoading ? (
              <div className="flex items-center justify-center h-[314px]">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-game-accent/30 border-t-game-accent rounded-full animate-spin mx-auto mb-3" />
                  <PixelText size="sm" color="muted">Generating {selectedCard.name} card...</PixelText>
                </div>
              </div>
            ) : selectedCard?.error ? (
              <div className="flex items-center justify-center h-[314px]">
                <PixelText size="sm" color="loss">{selectedCard.error}</PixelText>
              </div>
            ) : selectedCard?.imageUrl ? (
              <img
                src={selectedCard.imageUrl}
                alt={`${selectedCard.name} Share Card`}
                className="w-full h-auto"
                style={{ imageRendering: 'crisp-edges' }}
              />
            ) : null}
          </div>

          {/* Tweet Preview */}
          {selectedCard && (
            <div className="p-3 bg-game-panel border border-game-border">
              <PixelText size="xs" color="muted" className="mb-2">TWEET PREVIEW:</PixelText>
              <p className="font-retro text-sm text-game-text whitespace-pre-wrap">
                {selectedCard.tweetText}
              </p>
            </div>
          )}

          {/* All 3 Thumbnails */}
          <div className="grid grid-cols-3 gap-2">
            {cardOptions.map((card) => (
              <button
                key={card.id}
                onClick={() => setSelectedCardId(card.id)}
                className={`relative aspect-video border-2 transition-all overflow-hidden ${
                  selectedCardId === card.id
                    ? 'border-game-accent ring-2 ring-game-accent/50'
                    : 'border-game-border hover:border-game-muted'
                }`}
              >
                {card.isLoading ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-game-bg">
                    <div className="w-4 h-4 border-2 border-game-accent/30 border-t-game-accent rounded-full animate-spin" />
                  </div>
                ) : card.imageUrl ? (
                  <img
                    src={card.imageUrl}
                    alt={card.name}
                    className="w-full h-full object-cover"
                    style={{ imageRendering: 'crisp-edges' }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-game-bg">
                    <PixelText size="xs" color="loss">Error</PixelText>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 py-0.5">
                  <PixelText size="xs" color={selectedCardId === card.id ? 'accent' : 'muted'}>
                    {card.name}
                  </PixelText>
                </div>
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <RetroButton
              variant="primary"
              className="flex-1"
              onClick={handleShareTwitter}
              disabled={isGenerating || !selectedCard?.imageUrl}
            >
              SHARE TO X/TWITTER
            </RetroButton>
            <RetroButton
              variant="default"
              className="flex-1"
              onClick={handleDownload}
              disabled={isGenerating || !selectedCard?.imageUrl}
            >
              DOWNLOAD IMAGE
            </RetroButton>
            <RetroButton
              variant="default"
              onClick={handleCopyLink}
            >
              COPY LINK
            </RetroButton>
          </div>

          {/* Instructions */}
          <div className="text-center">
            <PixelText size="xs" color="muted">
              Download the image and attach it to your tweet for best results
            </PixelText>
          </div>
        </div>
      </RetroWindow>
    </div>
  )
}

// Helper functions
function getRandomFrom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)] as T
}

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  return `$${value.toFixed(0)}`
}

export default ShareCardPreview
